// Shared helpers for the activity-logging forms (Log / Bulk / Edit).
// Kept in one place so the single-activity and bulk paths can never drift apart.

// Minimum words a student must write so there is a "decent description" for the report.
// The AI summary is OPTIONAL — this floor applies to the user's own answers instead.
// NOTE: confirm the exact value with the client; change here and it applies everywhere.
export const MIN_DESCRIPTION_WORDS = 3;

// Max words for "What did you do?" / "What did you learn?" (each).
export const MAX_INPUT_WORDS = 50;

// Max words for the editable description box. Equals the combine of the two inputs
// (50 + 50) so the auto-combined fallback always fits.
export const MAX_DESCRIPTION_WORDS = 100;

export const countWords = (text = "") =>
  (text || "").trim().split(/\s+/).filter((word) => word.length > 0).length;

// Required free-text input ("what did you do / learn"): min + max words.
export const inputFieldValidator = (Yup) =>
  Yup.string()
    .required("Required")
    .test(
      "min-words",
      `Please write at least ${MIN_DESCRIPTION_WORDS} words`,
      (val) => countWords(val) >= MIN_DESCRIPTION_WORDS
    )
    .test(
      "max-words",
      `Maximum ${MAX_INPUT_WORDS} words allowed`,
      (val) => countWords(val) <= MAX_INPUT_WORDS
    );

// Editable description box: OPTIONAL. If left blank we fall back to combining the
// two inputs above, so the only checks are "decent if provided" + max length.
export const descriptionValidator = (Yup) =>
  Yup.string()
    .nullable()
    .test(
      "min-words",
      `Please write at least ${MIN_DESCRIPTION_WORDS} words or leave it blank`,
      (val) => !val || !val.trim() || countWords(val) >= MIN_DESCRIPTION_WORDS
    )
    .test(
      "max-words",
      `Maximum ${MAX_DESCRIPTION_WORDS} words allowed`,
      (val) => countWords(val) <= MAX_DESCRIPTION_WORDS
    );

// Build the description-related fields for the API payload.
// Precedence for the saved description: typed/AI box -> else combine the two inputs.
// `ai_summary` is stored ONLY when it was AI-generated (keeps the AI 50-word cap meaningful);
// a hand-written description lives in `description`, not `ai_summary`.
export const buildDescriptionFields = (values = {}) => {
  const box = (values.ai_summary || "").trim();
  const combined = `${(values.what_did_you_do || "").trim()} ${(values.what_did_you_learn || "").trim()}`.trim();
  const description = box || combined;
  const aiUsed = !!box && values.ai_summary_tagged === true;
  return {
    description,
    ai_summary: aiUsed ? box : null,
    ai_summary_tagged: aiUsed,
  };
};

// The description preview shown in the bulk list/review (resolved the same way).
export const resolveDescription = (values = {}) => buildDescriptionFields(values).description;
